import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AgentKind } from '../agentKind';

/**
 * chatSessionStore
 * ----------------
 * Source of truth for *which* Soteria chat is active and for brand-new
 * chats the user opened but hasn't sent in yet. The chat *data* (session
 * list + messages) lives server-side and is fetched via react-query in
 * `useSoteriaChat` — this store only holds the pointer + local drafts.
 *
 * The active pointer is kept PER AGENT KIND (`activeByKind`): the client
 * assistant (on /clients) and the financial assistant (elsewhere) are separate
 * conversations, so switching routes must resume the correct kind's thread —
 * never cross a client chat into the strategy agent. Drafts carry their kind
 * for the same reason (the sidebar for a kind shows only that kind's drafts).
 *
 * Persisted (localStorage) so a refresh resumes the same chats. We persist only
 * the pointers + drafts — never message content.
 */
export interface DraftSession {
  id: string;
  kind: AgentKind;
  createdAt: string; // ISO
}

/** A workspace strategy attached to a chat for Soteria to review/compare. */
export interface FocusStrategy {
  id: string;
  name: string;
}

interface ChatSessionStore {
  /** Active session id per agent kind (null = no active session for that kind). */
  activeByKind: Record<AgentKind, string | null>;
  drafts: Array<DraftSession>;
  /**
   * Strategies the user attached to each chat, keyed by session id. Sent to the
   * agent (ids) each turn so the focus persists for the whole conversation, and
   * rendered as removable chips above the composer (names). Only for
   * strategy-capable kinds (see STRATEGY_CAPABLE_KINDS in ../agentKind).
   */
  focusBySession: Record<string, Array<FocusStrategy>>;
  setActive: (kind: AgentKind, id: string | null) => void;
  /** Create a new draft chat for a kind, make it that kind's active, return its id. */
  addDraft: (kind: AgentKind) => string;
  removeDraft: (id: string) => void;
  /** Replace the focus set for a session (empty/undefined clears it). */
  setFocus: (id: string, strategies: Array<FocusStrategy>) => void;
  /** Drop focus entries whose session no longer exists, to bound the persisted
   *  map (otherwise never pruned — see removeDraft). Pass every known id:
   *  server sessions ∪ local drafts ∪ the active session. */
  pruneFocus: (knownSessionIds: Array<string>) => void;
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const EMPTY_ACTIVE: Record<AgentKind, string | null> = {
  soteria: null,
  // Legacy specialist kinds — no longer selectable in the UI, kept so any
  // persisted state from before the supervisor still rehydrates cleanly.
  financial: null,
  client: null,
};

export const useChatSessionStore = create<ChatSessionStore>()(
  persist(
    (set) => ({
      activeByKind: { ...EMPTY_ACTIVE },
      drafts: [],
      focusBySession: {},
      setActive: (kind, id) =>
        set((s) => ({ activeByKind: { ...s.activeByKind, [kind]: id } })),
      addDraft: (kind) => {
        const id = newSessionId();
        set((s) => ({
          drafts: [{ id, kind, createdAt: new Date().toISOString() }, ...s.drafts],
          activeByKind: { ...s.activeByKind, [kind]: id },
        }));
        return id;
      },
      removeDraft: (id) =>
        // NOTE: intentionally does NOT clear focusBySession[id]. A draft's id
        // becomes the real session id once its first turn is sent, and `send`
        // calls removeDraft to promote draft→server session — clearing focus
        // here would wipe it after turn one, so later turns would stop sending
        // the attached strategies. Orphaned focus for an abandoned empty draft
        // is bounded by pruneFocus (run once history loads).
        set((s) => ({ drafts: s.drafts.filter((d) => d.id !== id) })),
      setFocus: (id, strategies) =>
        set((s) => ({
          focusBySession: { ...s.focusBySession, [id]: strategies },
        })),
      pruneFocus: (knownSessionIds) =>
        set((s) => {
          const keep = new Set(knownSessionIds);
          const next: Record<string, Array<FocusStrategy>> = {};
          for (const id of Object.keys(s.focusBySession)) {
            if (keep.has(id)) next[id] = s.focusBySession[id];
          }
          return { focusBySession: next };
        }),
    }),
    {
      name: 'soteria-session-v1',
      version: 2,
      // v1 stored a single `activeSessionId` (financial only) and un-kinded
      // drafts. Map them onto the per-kind shape so an in-flight chat survives.
      migrate: (persisted, version) => {
        if (version < 2 && persisted) {
          const p = persisted as {
            activeSessionId?: string | null;
            drafts?: Array<{ id: string; createdAt: string; kind?: AgentKind }>;
            focusBySession?: Record<string, Array<FocusStrategy>>;
          };
          return {
            activeByKind: {
              ...EMPTY_ACTIVE,
              soteria: p.activeSessionId ?? null,
            },
            drafts: (p.drafts ?? []).map((d) => ({
              id: d.id,
              kind: d.kind ?? 'soteria',
              createdAt: d.createdAt,
            })),
            focusBySession: p.focusBySession ?? {},
          };
        }
        return persisted;
      },
      partialize: (s) => ({
        activeByKind: s.activeByKind,
        drafts: s.drafts,
        focusBySession: s.focusBySession,
      }),
    },
  ),
);
